import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiConnectorGet, apiConnectorPost } from "../../services/apiconnector";
import { endpoint } from "../../services/urls";
import toast from "react-hot-toast";

// ── shared tokens ──────────────────────────────────────────────────────────────
const C = {
  pageBg:      "#000000",
  cardBg:      "#131920",
  inputBg:     "#0d1117",
  border:      "1px solid rgba(255,215,0,0.18)",
  borderFocus: "1px solid rgba(255,215,0,0.55)",
  borderSubtle:"1px solid rgba(255,255,255,0.06)",
  accent:      "#00e5ff",          // cyan header
  gold:        "#f5a623",
  green:       "#00b578",
  greenBtn:    "linear-gradient(135deg, #00b578 0%, #00e676 100%)",
  textPrimary: "#ffffff",
  textMuted:   "#9ca3af",
  textDim:     "#6b7280",
};

function fmtDate(str) {
  if (!str) return "-";
  const d = new Date(str);
  return d.toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function shortAddr(addr) {
  if (!addr || addr.length <= 18) return addr || "-";
  return `${addr.slice(0, 8)}...${addr.slice(-6)}`;
}

// ── Topup form ─────────────────────────────────────────────────────────────────
function TopupForm() {
  const [amount, setAmount]             = useState("");
  const [qrCode, setQrCode]             = useState(null);
  const [walletAddress, setWalletAddress] = useState("");
  const [loading, setLoading]           = useState(false);
  const [submitting, setSubmitting]     = useState(false);
  const [showSuccess, setShowSuccess]   = useState(false);
  const [successMsg, setSuccessMsg]     = useState("");

  const handleGenerateQR = async () => {
    const parsed = parseFloat(amount);
    if (!amount || isNaN(parsed) || parsed <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    setLoading(true);
    const res = await apiConnectorPost(endpoint.get_topup_qr, { pkg_amount: parsed });
    setLoading(false);
    if (res?.data?.result?.[0]?.qr) {
      setQrCode(res.data.result[0].qr);
      setWalletAddress(res.data.result[0].address || "");
    } else {
      toast.error(res?.data?.message || "Failed to generate QR code");
    }
  };

  const handleSubmitTransaction = async () => {
    const parsed = parseFloat(amount);
    if (!amount || isNaN(parsed) || parsed <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    setSubmitting(true);
    const res = await apiConnectorGet(
      `${endpoint.member_self_topup}?pkg_amount=${parsed}`
    );
    setSubmitting(false);
    if (res?.data) {
      setSuccessMsg(res.data.message || "Transaction submitted successfully!");
      setShowSuccess(true);
    } else {
      toast.error(res?.msg || "Transaction failed. Please try again.");
    }
  };

  const copyAddress = () => {
    if (!walletAddress) return;
    navigator.clipboard.writeText(walletAddress).then(() => toast.success("Address copied!"));
  };

  const resetForm = () => {
    setShowSuccess(false);
    setAmount("");
    setQrCode(null);
    setWalletAddress("");
    setSuccessMsg("");
  };

  return (
    <>
      {/* Section label */}
      <p style={{
        fontSize: "11px", letterSpacing: "2.5px", color: C.textDim,
        marginBottom: "18px", textAlign: "center", textTransform: "uppercase",
      }}>
        ENTER AMOUNT
      </p>

      {/* Field label */}
      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
        <span style={{
          width: "22px", height: "22px", borderRadius: "50%",
          background: "rgba(0,229,255,0.08)", border: "1px solid rgba(0,229,255,0.25)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: C.accent, fontSize: "15px", fontWeight: "bold", flexShrink: 0,
        }}>+</span>
        <span style={{ color: "#d1d5db", fontSize: "13.5px" }}>
          Amount to Top Up(USDT.BEP20)
        </span>
      </div>

      {/* Amount input */}
      <input
        type="number"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        placeholder="0.00"
        min="0"
        style={{
          width: "100%", padding: "17px 16px", background: C.inputBg,
          border: C.border, borderRadius: "12px",
          color: amount ? C.textPrimary : C.textDim,
          fontSize: "20px", fontWeight: "500", outline: "none",
          boxSizing: "border-box", marginBottom: "20px", fontFamily: "inherit",
          transition: "border-color 0.2s",
        }}
        onFocus={(e) => { e.target.style.border = C.borderFocus; }}
        onBlur={(e)  => { e.target.style.border = C.border; }}
      />

      {/* QR Code panel */}
      {qrCode && (
        <div style={{
          background: C.inputBg, borderRadius: "14px", border: C.border,
          padding: "20px 16px", marginBottom: "20px", textAlign: "center",
        }}>
          <p style={{
            color: C.textDim, fontSize: "10px", letterSpacing: "1.5px",
            marginBottom: "14px", textTransform: "uppercase",
          }}>
            SCAN TO SEND USDT (BEP20)
          </p>
          <img
            src={qrCode}
            alt="QR Code"
            style={{
              width: "180px", height: "180px", borderRadius: "10px",
              background: "#fff", padding: "8px", boxSizing: "border-box",
              display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px",
            }}
          />
          {walletAddress && (
            <>
              <p style={{
                color: C.textDim, fontSize: "10px", margin: "14px 0 6px",
                letterSpacing: "1.5px", textTransform: "uppercase",
              }}>
                WALLET ADDRESS
              </p>
              <div
                onClick={copyAddress}
                style={{
                  background: "#1d1b1b", borderRadius: "10px", padding: "10px 12px",
                  cursor: "pointer", wordBreak: "break-all", fontSize: "11px",
                  color: C.gold, border: C.border,
                  display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                }}
              >
                <span style={{ flex: 1, textAlign: "left" }}>{walletAddress}</span>
                <span style={{ fontSize: "14px", flexShrink: 0 }}>📋</span>
              </div>
            </>
          )}
        </div>
      )}

      {/* Generate QR button */}
      <button
        onClick={handleGenerateQR}
        disabled={loading}
        style={{
          width: "100%", padding: "17px",
          background: loading ? "rgba(0,181,120,0.35)" : C.greenBtn,
          border: "none", borderRadius: "12px", color: "#fff",
          fontSize: "16px", fontWeight: "700",
          cursor: loading ? "not-allowed" : "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", gap: "10px",
          marginBottom: "12px", letterSpacing: "0.3px",
          boxShadow: loading ? "none" : "0 4px 18px rgba(0,181,120,0.3)",
        }}
      >
        <span style={{ fontSize: "17px" }}>⊞</span>
        {loading ? "Generating..." : "Generate Qr Code"}
      </button>

      {/* Submit Transaction button */}
      <button
        onClick={handleSubmitTransaction}
        disabled={submitting}
        style={{
          width: "100%", padding: "17px",
          background: submitting ? "rgba(0,181,120,0.35)" : C.greenBtn,
          border: "none", borderRadius: "12px", color: "#fff",
          fontSize: "16px", fontWeight: "700",
          cursor: submitting ? "not-allowed" : "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", gap: "10px",
          letterSpacing: "0.3px",
          boxShadow: submitting ? "none" : "0 4px 18px rgba(0,181,120,0.3)",
        }}
      >
        <span>✓</span>
        {submitting ? "Submitting..." : "Submit Transaction"}
        <span>→</span>
      </button>

      {/* Success popup */}
      {showSuccess && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)",
          backdropFilter: "blur(8px)", display: "flex", alignItems: "center",
          justifyContent: "center", zIndex: 9999, padding: "20px",
        }}>
          <div style={{
            background: C.cardBg, borderRadius: "20px",
            border: "1px solid rgba(0,181,120,0.4)",
            boxShadow: "0 0 60px rgba(0,181,120,0.15)",
            padding: "40px 28px 32px", textAlign: "center",
            maxWidth: "340px", width: "100%",
          }}>
            <div style={{
              width: "66px", height: "66px", borderRadius: "50%",
              background: "rgba(0,181,120,0.12)", border: "2px solid #00b578",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 20px", fontSize: "28px", color: C.green,
              boxShadow: "0 0 20px rgba(0,181,120,0.2)",
            }}>✓</div>
            <h3 style={{ color: C.textPrimary, fontSize: "20px", fontWeight: "700", marginBottom: "10px" }}>
              Transaction Submitted
            </h3>
            <p style={{ color: C.textMuted, fontSize: "14px", marginBottom: "28px", lineHeight: 1.6 }}>
              {successMsg}
            </p>
            <button
              onClick={resetForm}
              style={{
                width: "100%", padding: "15px", background: C.greenBtn,
                border: "none", borderRadius: "12px", color: "#fff",
                fontSize: "15px", fontWeight: "700", cursor: "pointer",
                boxShadow: "0 4px 18px rgba(0,181,120,0.3)",
              }}
            >Done</button>
          </div>
        </div>
      )}
    </>
  );
}

// ── History view ────────────────────────────────────────────────────────────────
function HistoryView() {
  const [page, setPage]         = useState(1);
  const [claimingId, setClaimingId] = useState(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["topup_history", page],
    queryFn: () =>
      apiConnectorPost(endpoint.get_wallet_transactions, { page, count: "10" }),
    keepPreviousData: true,
  });

  const rows       = data?.data?.result?.data || [];
  const totalPages = Math.max(1, data?.data?.result?.total_pages || 1);

  const handleClaim = async (uwId) => {
    if (!window.confirm("Claim this transaction?")) return;
    setClaimingId(uwId);
    const res = await apiConnectorPost(endpoint.member_claimed_pending_transaction, { t_id: uwId });
    setClaimingId(null);
    if (res?.data?.success) {
      toast.success(res.data.msg || "Claimed successfully!");
      queryClient.invalidateQueries(["topup_history"]);
    } else {
      toast.error(res?.data?.msg || "Claim failed");
    }
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", padding: "40px 0" }}>
        <div style={{
          width: "34px", height: "34px", borderRadius: "50%",
          border: `3px solid rgba(245,166,35,0.15)`,
          borderTopColor: C.gold,
          animation: "spin 0.8s linear infinite",
          margin: "0 auto",
        }} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "40px 0" }}>
        <div style={{ fontSize: "34px", marginBottom: "12px" }}>📋</div>
        <p style={{ color: C.textDim, fontSize: "14px" }}>No transactions yet</p>
      </div>
    );
  }

  return (
    <div>
      {rows.map((row, i) => (
        <div
          key={row?.m06_uw_id || i}
          style={{
            background: C.inputBg, borderRadius: "12px",
            border: C.border, padding: "14px", marginBottom: "10px",
          }}
        >
          {/* TX id + amount */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "7px" }}>
            <span style={{ color: C.textDim, fontSize: "11px" }}>
              #{row?.m06_trans_id || "-"}
            </span>
            <span style={{ color: C.green, fontWeight: "700", fontSize: "15px" }}>
              {parseFloat(row?.m06_rece_amount || 0).toFixed(3)} USDT
            </span>
          </div>

          {/* Date */}
          <p style={{ color: C.textDim, fontSize: "11px", marginBottom: "8px" }}>
            {fmtDate(row?.m06_created_at)}
          </p>

          {/* Wallet + copy */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            background: "#1d1b1b", borderRadius: "8px", padding: "7px 10px", marginBottom: "8px",
            border: "1px solid rgba(255,215,0,0.10)",
          }}>
            <span style={{ color: C.gold, fontSize: "11px", flex: 1, marginRight: "8px" }}>
              {shortAddr(row?.m06_wallet)}
            </span>
            <button
              onClick={() => {
                navigator.clipboard.writeText(row?.m06_wallet || "");
                toast.success("Copied!");
              }}
              style={{
                background: "rgba(245,166,35,0.08)", border: "1px solid rgba(245,166,35,0.2)",
                borderRadius: "6px", color: C.gold, fontSize: "10px",
                padding: "3px 8px", cursor: "pointer", flexShrink: 0,
              }}
            >Copy</button>
          </div>

          {/* Status + BSCScan */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            {row?.m06_is_claimed === 0 ? (
              <button
                onClick={() => handleClaim(row?.m06_uw_id)}
                disabled={claimingId === row?.m06_uw_id}
                style={{
                  background: C.greenBtn, border: "none", borderRadius: "8px",
                  color: "#fff", fontSize: "12px", fontWeight: "700",
                  padding: "6px 18px", cursor: claimingId === row?.m06_uw_id ? "not-allowed" : "pointer",
                  opacity: claimingId === row?.m06_uw_id ? 0.6 : 1,
                  boxShadow: "0 2px 10px rgba(0,181,120,0.25)",
                }}
              >
                {claimingId === row?.m06_uw_id ? "Claiming..." : "Claim"}
              </button>
            ) : (
              <span style={{
                background: "rgba(107,114,128,0.12)", borderRadius: "8px",
                color: C.textMuted, fontSize: "12px", fontWeight: "600", padding: "6px 18px",
                border: "1px solid rgba(107,114,128,0.2)",
              }}>Claimed</span>
            )}

            {row?.tr_hex_code ? (
              <button
                onClick={() => window.open(`https://bscscan.com/tx/${row.tr_hex_code}`, "_blank")}
                style={{
                  background: "rgba(245,166,35,0.06)", border: "1px solid rgba(245,166,35,0.2)",
                  borderRadius: "8px", color: C.gold, fontSize: "11px",
                  padding: "6px 12px", cursor: "pointer",
                }}
              >
                BSCScan ↗
              </button>
            ) : (
              <span style={{ color: "#374151", fontSize: "11px" }}>No hash</span>
            )}
          </div>
        </div>
      ))}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "12px", marginTop: "16px" }}>
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{
              background: "rgba(245,166,35,0.06)", border: "1px solid rgba(245,166,35,0.2)",
              borderRadius: "8px", color: page === 1 ? "#374151" : C.gold,
              fontSize: "13px", padding: "7px 16px",
              cursor: page === 1 ? "not-allowed" : "pointer",
            }}
          >← Prev</button>
          <span style={{ color: C.textDim, fontSize: "12px" }}>{page} / {totalPages}</span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            style={{
              background: "rgba(245,166,35,0.06)", border: "1px solid rgba(245,166,35,0.2)",
              borderRadius: "8px", color: page === totalPages ? "#374151" : C.gold,
              fontSize: "13px", padding: "7px 16px",
              cursor: page === totalPages ? "not-allowed" : "pointer",
            }}
          >Next →</button>
        </div>
      )}
    </div>
  );
}

// ── Main export ─────────────────────────────────────────────────────────────────
export default function Topup() {
  const [view, setView] = useState("form");

  return (
    <div style={{
      minHeight: "100vh", background: C.pageBg, display: "flex",
      flexDirection: "column", alignItems: "center", justifyContent: "center",
      padding: "20px 16px 100px",
    }}>
      <div style={{
        width: "100%", maxWidth: "420px",
        background: C.cardBg, borderRadius: "20px",
        border: "1px solid rgba(255,215,0,0.12)",
        boxShadow: "0 8px 40px rgba(0,0,0,0.6)",
        overflow: "hidden",
      }}>

        {/* ── Card header ── */}
        <div style={{ padding: "26px 24px 20px" }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
            {/* Left: dot + title */}
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
                <span style={{
                  width: "10px", height: "10px", borderRadius: "50%", background: C.accent,
                  boxShadow: `0 0 8px ${C.accent}`, flexShrink: 0,
                }} />
                <span style={{ fontSize: "19px", fontWeight: "800", color: C.accent, letterSpacing: "-0.3px" }}>
                  Accept Only USDT.BEP20
                </span>
              </div>
              <p style={{ color: C.textDim, fontSize: "13px", marginLeft: "20px", lineHeight: 1.5 }}>
                Add funds to your fund wallet
              </p>
            </div>

            {/* History / Back toggle */}
            <button
              onClick={() => setView(view === "form" ? "history" : "form")}
              style={{
                flexShrink: 0, marginLeft: "12px",
                background: view === "history" ? "rgba(245,166,35,0.12)" : "rgba(245,166,35,0.06)",
                border: "1px solid rgba(245,166,35,0.3)",
                borderRadius: "10px", color: C.gold, fontSize: "12px", fontWeight: "600",
                padding: "7px 13px", cursor: "pointer",
                display: "flex", alignItems: "center", gap: "5px",
                whiteSpace: "nowrap", transition: "background 0.2s",
              }}
            >
              {view === "form" ? <>📋 History</> : <>← Back</>}
            </button>
          </div>

          {/* Divider with center dot */}
          <div style={{ position: "relative", margin: "20px 0 0", display: "flex", alignItems: "center" }}>
            <div style={{ flex: 1, height: "1px", background: "rgba(255,215,0,0.12)" }} />
            <span style={{
              width: "8px", height: "8px", borderRadius: "50%", background: C.accent,
              boxShadow: `0 0 6px ${C.accent}`, flexShrink: 0, position: "relative", zIndex: 1,
            }} />
            <div style={{ flex: 1, height: "1px", background: "rgba(255,215,0,0.12)" }} />
          </div>
        </div>

        {/* ── Card body ── */}
        <div style={{ padding: "0 24px 28px" }}>
          {view === "form" ? <TopupForm /> : <HistoryView />}
        </div>
      </div>
    </div>
  );
}
